import React from 'react';
import { portfolioData } from '../mock';
import { Award, Target, Users, Zap, FileText, DollarSign } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Target size={32} />,
      title: 'Strategic Thinking',
      description: 'Every design decision is purposeful, aligned with brand objectives and user needs.'
    },
    {
      icon: <Zap size={32} />,
      title: 'Innovation',
      description: 'Pushing boundaries and exploring new techniques to create fresh, memorable experiences.'
    },
    {
      icon: <Award size={32} />,
      title: 'Excellence',
      description: 'Commitment to craft, attention to detail, and continuous refinement of skills.'
    },
    {
      icon: <Users size={32} />,
      title: 'Collaboration',
      description: 'Working closely with teams and clients to bring visions to life effectively.'
    }
  ];

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                About <span className="text-amber-400">Me</span>
              </h1>
              <div className="w-20 h-1 bg-amber-400 mb-8 rounded-full" />
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                {portfolioData.aboutText}
              </p>
              
              {/* Download Buttons */}
              <div className="flex flex-wrap gap-4">
                <a
                  href="/downloads/scott-parrish-resume.pdf"
                  download="Scott_Parrish_Resume_2026.pdf"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-400 text-black font-semibold rounded-full hover:bg-amber-300 transition-all duration-300"
                  data-testid="download-resume-btn"
                >
                  <FileText size={20} />
                  Download Resume
                </a>
                <a
                  href="/downloads/scott-parrish-rate-card.pdf"
                  download="Scott_Parrish_Rate_Card_2026.pdf"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-full border border-white/20 hover:bg-white/20 hover:border-amber-400/50 transition-all duration-300"
                  data-testid="download-rate-card-btn"
                >
                  <DollarSign size={20} />
                  View Rate Card
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border border-amber-400/20">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-transparent" />
                <div className="bg-gradient-to-br from-zinc-900 to-black p-12">
                  <blockquote className="text-3xl font-bold text-white mb-4">
                    "{portfolioData.philosophy}"
                  </blockquote>
                  <p className="text-amber-400 font-semibold">— My Guiding Principle</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Core <span className="text-amber-400">Values</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              The principles that guide my creative process and define my approach to visual design.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 hover:border-amber-400/30 rounded-2xl p-8 transition-all duration-500 hover:-translate-y-2"
              >
                <div className="text-amber-400 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-gray-400 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-24 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Expertise <span className="text-amber-400">Areas</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Visual Communication',
              'Brand Identity',
              'Print Design',
              'Environmental Graphics',
              'Package Design',
              'Infographics',
              'Event Design',
              'Illustration',
              'Direct Mail Campaigns'
            ].map((skill, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 hover:border-amber-400/30 rounded-xl p-6 text-center transition-all duration-300 hover:bg-white/10"
              >
                <p className="text-white font-semibold text-lg">{skill}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Create Something
            <span className="block text-amber-400">Extraordinary Together</span>
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            I invite you to connect with me to discuss my work and explore potential collaborations.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-amber-400 text-black font-semibold rounded-full hover:bg-amber-300 transition-all duration-300"
          >
            Start a Conversation
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;